import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

declare global {
  interface Window {
    __onixAnalyticsLoaded?: boolean;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Call this once in your app startup (e.g. main.tsx / main.jsx / App.tsx)
 */
export function initCookieConsent() {
  console.log('COOKIE_DEBUG: Initializing CookieConsent...');
  CookieConsent.run({
    guiOptions: {
      consentModal: {
        layout: 'box',
        position: 'bottom right',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' },
            { name: /^_gat/ },
          ],
        },
      },
      marketing: {
        autoClear: {
          cookies: [
            { name: '_fbp' },
            { name: '_fbc' },
          ],
        },
      },
    },

    language: {
      default: 'ca',
      autoDetect: 'document',
      translations: {
        ca: {
          consentModal: {
            title: 'Cookies a Onix Digital',
            description:
              'Utilitzem cookies necessàries per al funcionament del web i, només si ho acceptes, cookies d\'anàlisi i màrqueting. Pots canviar la teua decisió en qualsevol moment des de l\'enllaç “Configuració de cookies”.',
            acceptAllBtn: 'Acceptar totes',
            acceptNecessaryBtn: 'Rebutjar opcionals',
            showPreferencesBtn: 'Configurar',
            footer:
              '<a href="/politica-privacitat">Política de privacitat</a>\n<a href="/politica-cookies">Política de cookies</a>\n<a href="/avis-legal">Avís legal</a>',
          },
          preferencesModal: {
            title: 'Configuració de cookies',
            acceptAllBtn: 'Acceptar totes',
            acceptNecessaryBtn: 'Rebutjar opcionals',
            savePreferencesBtn: 'Guardar configuració',
            closeIconLabel: 'Tancar',
            sections: [
              {
                title: 'Ús de cookies',
                description:
                  'Pots decidir quines categories de cookies acceptes. Les necessàries sempre estan actives perquè la web funcione correctament.',
              },
              {
                title: 'Cookies necessàries',
                description:
                  'Imprescindibles per al funcionament bàsic del lloc web i per recordar la teua decisió sobre el consentiment.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Cookies d\'anàlisi',
                description:
                  'Ens ajuden a entendre com es fa servir la web per millorar continguts, rendiment i conversions.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Cookies de màrqueting',
                description:
                  'Serveixen per mesurar campanyes o mostrar accions comercials més rellevants en plataformes externes.',
                linkedCategory: 'marketing',
              },
              {
                title: 'Més informació',
                description:
                  'Consulta la nostra <a href="/politica-cookies">Política de cookies</a> per a més detall.',
              },
            ],
          },
        },
        es: {
          consentModal: {
            title: 'Cookies en Onix Digital',
            description:
              'Utilizamos cookies necesarias para el funcionamiento de la web y, solo si lo aceptas, cookies de analítica y marketing. Puedes cambiar tu decisión en cualquier momento desde el enlace “Configuración de cookies”.',
            acceptAllBtn: 'Aceptar todas',
            acceptNecessaryBtn: 'Rechazar opcionales',
            showPreferencesBtn: 'Configurar',
            footer:
              '<a href="/politica-privacitat">Política de privacidad</a>\n<a href="/politica-cookies">Política de cookies</a>\n<a href="/avis-legal">Aviso legal</a>',
          },
          preferencesModal: {
            title: 'Configuración de cookies',
            acceptAllBtn: 'Aceptar todas',
            acceptNecessaryBtn: 'Rechazar opcionales',
            savePreferencesBtn: 'Guardar configuración',
            closeIconLabel: 'Cerrar',
            sections: [
              {
                title: 'Uso de cookies',
                description:
                  'Puedes decidir qué categorías de cookies aceptas. Las necesarias siempre están activas porque la web las necesita para funcionar correctamente.',
              },
              {
                title: 'Cookies necesarias',
                description:
                  'Imprescindibles para el funcionamiento básico del sitio web y para recordar tu decisión sobre el consentimiento.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Cookies de analítica',
                description:
                  'Nos ayudan a entender cómo se usa la web para mejorar contenidos, rendimiento y conversiones.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Cookies de marketing',
                description:
                  'Sirven para medir campañas o mostrar acciones comerciales más relevantes en plataformas externas.',
                linkedCategory: 'marketing',
              },
              {
                title: 'Más información',
                description:
                  'Consulta nuestra <a href="/politica-cookies">Política de cookies</a> para más detalle.',
              },
            ],
          },
        },
        en: {
          consentModal: {
            title: 'Cookies on Onix Digital',
            description:
              'We use necessary cookies for the website to work and, only if you accept, analytics and marketing cookies. You can change your choice at any time from the “Cookie settings” link.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject optional',
            showPreferencesBtn: 'Settings',
            footer:
              '<a href="/politica-privacitat">Privacy policy</a>\n<a href="/politica-cookies">Cookie policy</a>\n<a href="/avis-legal">Legal notice</a>',
          },
          preferencesModal: {
            title: 'Cookie settings',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject optional',
            savePreferencesBtn: 'Save settings',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'Cookie use',
                description:
                  'You can choose which cookie categories you accept. Necessary cookies are always active because the website needs them to work properly.',
              },
              {
                title: 'Necessary cookies',
                description:
                  'Essential for the basic operation of the website and to remember your consent choice.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics cookies',
                description:
                  'They help us understand how the website is used so we can improve content, performance and conversions.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing cookies',
                description:
                  'Used to measure campaigns or show more relevant commercial actions on external platforms.',
                linkedCategory: 'marketing',
              },
              {
                title: 'More information',
                description:
                  'See our <a href="/politica-cookies">Cookie policy</a> for more detail.',
              },
            ],
          },
        },
      },
    },

    onFirstConsent: () => {
      handleConsentSideEffects();
    },
    onConsent: () => {
      handleConsentSideEffects();
    },
    onChange: () => {
      handleConsentSideEffects();
    },
  });
}

function handleConsentSideEffects() {
  const analyticsAccepted = CookieConsent.acceptedCategory('analytics');
  const marketingAccepted = CookieConsent.acceptedCategory('marketing');

  if (analyticsAccepted) {
    loadGoogleAnalytics();
  }

  updateGoogleConsentMode({
    analyticsAccepted,
    marketingAccepted,
  });
}

function loadGoogleAnalytics() {
  console.log('COOKIE_DEBUG: Loading Google Analytics...');
  if (window.__onixAnalyticsLoaded) {
    console.log('COOKIE_DEBUG: GA already loaded, skipping.');
    return;
  }
  const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (!GA_ID) {
    console.error('COOKIE_DEBUG: GA_ID is missing!');
    return;
  }
  console.log('COOKIE_DEBUG: Initializing GA with ID:', GA_ID);
  window.__onixAnalyticsLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    anonymize_ip: true,
  });
}

function updateGoogleConsentMode({
  analyticsAccepted,
  marketingAccepted,
}: {
  analyticsAccepted: boolean;
  marketingAccepted: boolean;
}) {
  if (!window.gtag) return;

  window.gtag('consent', 'update', {
    analytics_storage: analyticsAccepted ? 'granted' : 'denied',
    ad_storage: marketingAccepted ? 'granted' : 'denied',
    ad_user_data: marketingAccepted ? 'granted' : 'denied',
    ad_personalization: marketingAccepted ? 'granted' : 'denied',
  });
}

export function showCookiePreferences() {
  CookieConsent.showPreferences();
}
